import { useState } from 'react';
import type { EffectiveModule, Entitlement } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../components/fields';
import { DateText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import {
  ENTITLEMENT_SOURCE_LABELS,
  addDays,
  endOfDay,
  formatDate,
  isDay,
  startOfDay,
  utcDay,
} from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope, useSupportAccessHint } from './scope';

/** The grants staff add here: time-limited, on top of the plan or contract. */
const GRANT_KINDS = [
  {
    value: 'trial',
    label: 'Trial',
    description: 'The organization tries the module for a limited time before buying it.',
  },
  {
    value: 'support_grant',
    label: 'Support grant',
    description: 'The module for a limited time to solve a problem, outside the contract.',
  },
] as const;
type GrantKind = (typeof GRANT_KINDS)[number]['value'];

/** Days a new grant lasts unless the staff member picks another end. */
const DEFAULT_GRANT_DAYS = 14;

interface GrantForm {
  moduleKey: string;
  kind: GrantKind;
  starts: string;
  ends: string;
  reason: string;
}

type GrantErrors = Partial<Record<keyof GrantForm, string>>;

function validateGrant(form: GrantForm, today: string): GrantErrors {
  const errors: GrantErrors = {};
  if (!form.moduleKey.trim()) errors.moduleKey = 'Choose a module.';
  if (!isDay(form.starts)) errors.starts = 'Enter the first day, as a date.';
  if (!isDay(form.ends)) errors.ends = 'Enter the last day, as a date.';
  else if (form.ends < today) errors.ends = 'The last day cannot be in the past.';
  else if (isDay(form.starts) && form.ends < form.starts)
    errors.ends = 'The last day cannot be before the first.';
  const reason = reasonError(form.reason);
  if (reason) errors.reason = reason;
  return errors;
}

function moduleName(modules: readonly EffectiveModule[] | null, key: string): string {
  return modules?.find((m) => m.module.key === key)?.module.name ?? key;
}

function EntitlementsTable({
  entitlements,
  modules,
  busy,
}: {
  entitlements: readonly Entitlement[];
  modules: readonly EffectiveModule[] | null;
  busy: boolean;
}) {
  return (
    <ScrollTable caption="Entitlements" busy={busy} className="admin-table admin-table--entitlements">
      <thead>
        <tr>
          <th scope="col">Module</th>
          <th scope="col">Source</th>
          <th scope="col">Status</th>
          <th scope="col">Starts</th>
          <th scope="col">Ends</th>
          <th scope="col">Granted by</th>
          <th scope="col">Reason</th>
        </tr>
      </thead>
      <tbody>
        {entitlements.map((entitlement) => (
          <tr key={entitlement.id}>
            <th scope="row">
              {moduleName(modules, entitlement.moduleKey)}
              <span className="admin-cell-secondary">
                <code>{entitlement.moduleKey}</code>
              </span>
            </th>
            <td>{ENTITLEMENT_SOURCE_LABELS[entitlement.source] ?? entitlement.source}</td>
            <td>
              <StatusBadge status={entitlement.status} />
            </td>
            <td>
              <DateText iso={entitlement.startsAt} />
            </td>
            <td>{entitlement.endsAt ? <DateText iso={entitlement.endsAt} /> : 'No end date'}</td>
            <td>{entitlement.grantedBy ?? <span className="admin-muted">System</span>}</td>
            <td className="admin-cell-wrap">
              {entitlement.reason ?? <span className="admin-muted">None</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

/**
 * Entitlements (/organizations/:id/entitlements): what the organization may use and why (plan, contract,
 * trial or support grant), with its status and dates. Staff who may manage billing add a trial or a
 * support grant for a limited time, with a reason; platform-api checks the module and its dependencies.
 */
export function EntitlementsTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api } = useAdmin();
  const organizationId = organization.id;
  const key = `entitlements\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listEntitlements(organizationId, signal));
  // The modules give names and the module choice; without them the form takes a module key.
  const modulesKey = `modules\u0000${organizationId}`;
  const modulesRead = useAdminRead(modulesKey, (signal) =>
    api.getOrganizationModules(organizationId, signal),
  );
  const modules =
    modulesRead.dataKey === modulesKey && !modulesRead.failure ? (modulesRead.data?.modules ?? null) : null;
  const entitlements = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<'grant'>();
  const [form, setForm] = useState<GrantForm>({
    moduleKey: '',
    kind: 'trial',
    starts: '',
    ends: '',
    reason: '',
  });
  const [errors, setErrors] = useState<GrantErrors>({});
  const set = <K extends keyof GrantForm>(field: K, value: GrantForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const openGrant = () => {
    const today = utcDay(Date.now());
    setForm({
      moduleKey: '',
      kind: 'trial',
      starts: today,
      ends: addDays(today, DEFAULT_GRANT_DAYS),
      reason: '',
    });
    setErrors({});
    dialog.show('grant');
  };

  const moduleOptions = [
    { value: '', label: 'Choose a module' },
    ...(modules ?? []).map((m) => ({ value: m.module.key, label: `${m.module.name} (${m.module.key})` })),
  ];

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Entitlements"
        description="What the organization may use: its plan or contract, plus trials and support grants for a limited time."
        action={
          abilities.grantEntitlements ? (
            <button type="button" className="admin-action admin-action--primary" onClick={openGrant}>
              Add a trial or support grant
            </button>
          ) : undefined
        }
      >
        {read.failure ? (
          <AdminProblem
            failure={read.failure}
            subject="the entitlements"
            onRetry={read.retry}
            supportAccess={hint}
          />
        ) : !entitlements ? (
          <LoadingBlock label="Loading the entitlements" />
        ) : entitlements.length === 0 ? (
          <EmptyPageState
            icon={<NavigationGlyph name="WalletCards" />}
            title="No entitlements yet"
            description="This organization has no plan, contract, trial or support grant for any module."
          />
        ) : (
          <EntitlementsTable entitlements={entitlements} modules={modules} busy={read.loading} />
        )}
      </PageSection>
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title="Add a trial or support grant"
        description={`A time-limited entitlement for ${organization.displayName}, on top of its plan or contract.`}
        submitLabel="Add the grant"
        onSubmit={async () => {
          const found = validateGrant(form, utcDay(Date.now()));
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          await api.grantEntitlement(organizationId, {
            moduleKey: form.moduleKey.trim(),
            source: form.kind,
            startsAt: startOfDay(form.starts),
            endsAt: endOfDay(form.ends),
            reason: form.reason,
          });
          return undefined;
        }}
        onSaved={() => {
          const kind = form.kind === 'trial' ? 'Trial' : 'Support grant';
          setNotice(
            `${kind} of ${moduleName(modules, form.moduleKey.trim())} added, from ${formatDate(startOfDay(form.starts))} to ${formatDate(endOfDay(form.ends))}.`,
          );
          read.retry();
          modulesRead.retry();
        }}
      >
        {modules ? (
          <SelectField
            label="Module"
            value={form.moduleKey}
            onChange={(value) => set('moduleKey', value)}
            options={moduleOptions}
            error={errors.moduleKey ?? null}
            required
          />
        ) : (
          <TextField
            label="Module key"
            value={form.moduleKey}
            onChange={(value) => set('moduleKey', value)}
            hint="For example enterprise.negotiation. The module list could not be loaded."
            error={errors.moduleKey ?? null}
            spellCheck={false}
            required
          />
        )}
        <RadioGroup
          legend="Kind"
          choices={GRANT_KINDS}
          value={form.kind}
          onChange={(value) => set('kind', value as GrantKind)}
        />
        <div className="admin-field-row">
          <TextField
            label="First day"
            type="date"
            value={form.starts}
            onChange={(value) => set('starts', value)}
            hint="From the start of the day, UTC."
            error={errors.starts ?? null}
            required
          />
          <TextField
            label="Last day"
            type="date"
            value={form.ends}
            onChange={(value) => set('ends', value)}
            hint="Until the end of the day, UTC."
            error={errors.ends ?? null}
            required
          />
        </div>
        <ReasonField
          value={form.reason}
          onChange={(value) => set('reason', value)}
          error={errors.reason ?? null}
        />
      </WriteDialog>
    </>
  );
}
